"use client";

import { useTransition, useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, usePathname } from "next/navigation";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function SelectLocale() {
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = useLocale();
  const t = useTranslations('General');
  const [, startTransition] = useTransition();
  
  // State to track locale change announcements for screen readers
  const [announcement, setAnnouncement] = useState<string>('');
  
  // Handle locale change with optimized navigation
  const handleChange = (newLocale: string) => {
    if (newLocale === currentLocale) return;
    
    // Create announcement for screen readers
    const newLocaleName = newLocale === 'en' ? t('english') : t('portuguese');
    setAnnouncement(t('selectLanguage') + ': ' + newLocaleName);
    
    startTransition(() => {
      // Extract path without current locale prefix
      const pathWithoutLocale = pathname.replace(/^\/[a-z]{2}/, '') || '';
      
      // Build new URL with search params and hash preserved
      const searchParams = new URLSearchParams(window.location.search);
      const hash = window.location.hash;
      const queryString = searchParams.toString();
      
      const newPath = `/${newLocale}${pathWithoutLocale}${queryString ? `?${queryString}` : ''}${hash}`;
      
      // Use replace to avoid adding to browser history
      router.replace(newPath);
    });
  };
  
  // Clear announcement after it's been read by screen readers
  useEffect(() => {
    if (announcement) {
      const timer = setTimeout(() => {
        setAnnouncement('');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [announcement]);
  
  return (
    <>
      {/* Accessibility announcement for screen readers */}
      {announcement && (
        <div 
          aria-live="polite" 
          className="sr-only"
          role="status"
        >
          {announcement}
        </div>
      )}
      
      <Select 
        defaultValue={currentLocale} 
        onValueChange={handleChange}
      >
        <SelectTrigger 
          className="w-17" 
          aria-label={t('selectLanguage')}
          title={t('selectLanguage')}
        >
          <SelectValue placeholder={currentLocale} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="en" title={t('english')}>EN</SelectItem>
          <SelectItem value="pt" title={t('portuguese')}>PT</SelectItem>
        </SelectContent>
      </Select>
    </>
  );
}

export default SelectLocale;